import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGate } from 'statsig-react';
import { twMerge } from 'tailwind-merge';
import { faUserCheck } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faUserPlus } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { FollowButtonSource } from '@soundxyz/utils/analytics';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { Button } from '@components/main-components/button/Button';
import { setFollowingStatus, useFollowingStatus } from '@components/modals/UserStats/store';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  ArtistProfileNewDocument,
  FollowStatusDocument,
  FollowUserDocument,
  GetArtistIdByUserIdDocument,
  GetUserNotificationPreferencesDocument,
  GetUserProfileNewDocument,
  gql,
  IsFollowingDocument,
  IsSubscribedToArtistsNotificationsDocument,
  SubscribeToArtistDocument,
  UnfollowUserDocument,
  UnsubscribeToArtistDocument,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { FEATURE_GATES } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query GetArtistIdByUserId($userId: UUID!) {
    user(id: $userId) {
      id
      artist {
        id
        name
      }
    }
  }

  mutation FollowUser($input: FollowUserInput!) {
    followUser(input: $input) {
      id
      userA {
        id
        username
        followerCount
        followingCount
      }
      userB {
        id
        username
        followerCount
        followingCount
      }
    }
  }

  mutation UnfollowUser($input: UnfollowUserInput!) {
    unfollowUser(input: $input) {
      id
      userA {
        id
        username
        followerCount
        followingCount
      }
      userB {
        id
        username
        followerCount
        followingCount
      }
    }
  }
`);

RefetchOperationsOnComplete({
  trigger: [FollowUserDocument, UnfollowUserDocument, SubscribeToArtistDocument],
  refetch: [
    FollowStatusDocument,
    IsFollowingDocument,
    IsSubscribedToArtistsNotificationsDocument,
    GetUserNotificationPreferencesDocument,
    GetUserProfileNewDocument,
    ArtistProfileNewDocument,
  ],
});

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2491/add-tests-for-followbutton
export const FollowButton = ({
  userId,
  fullWidth = false,
  size = 'L',
  variant = 'primary',
  dark = false,
  className,
  sourceLocation,
  useIcon = false,
}: {
  userId: string;
  size?: 'XS' | 'S' | 'M' | 'L';
  /**
   * @default primary
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'notification';
  fullWidth?: boolean;
  dark?: boolean;
  className?: string;
  sourceLocation: FollowButtonSource;
  useIcon?: boolean;
}) => {
  const { authUser } = useAuth();
  const { login } = useConnectedAuth();

  const { value: showNotificationsFeatureGate } = useGate(FEATURE_GATES.EMAIL_NOTIFICATIONS);

  const { data: artistData } = useQuery(GetArtistIdByUserIdDocument, {
    variables: {
      userId,
    },
    enabled: showNotificationsFeatureGate,
    staleTime: 0,
  });

  const artistId = artistData?.data.user?.artist?.id;

  const { mutateAsync: followUserMutation } = useMutation(FollowUserDocument, {});
  const { mutateAsync: unfollowUserMutation } = useMutation(UnfollowUserDocument, {});
  const { mutateAsync: unsubscribeToArtist } = useMutation(UnsubscribeToArtistDocument, {});

  const logError = useLogError();
  const { openToast } = useToast();

  const followingStatus = useFollowingStatus({
    userId,
  });

  const label = followingStatus === true ? 'Following' : 'Follow';

  //hides the follow button if the user is the same as the current user
  if (authUser?.id === userId) {
    return null;
  }
  const loading = followingStatus === 'loading';

  const handleFollow = () => {
    if (followingStatus === true) {
      trackClickEvent({
        event: 'Unfollow Clicked',
        properties: {
          associationId: userId,
          associationType: 'User',
          sourceComponent: sourceLocation,
        },
      });
      setFollowingStatus({
        userId,
        isFollowing: false,
      });
      unfollowUserMutation(
        {
          input: {
            user: userId,
          },
        },
        {
          onSuccess: ({ data }) => {
            openToast({
              text: `You unfollowed ${data.unfollowUser.userB.username}.`,
              variant: 'success',
            });

            if (showNotificationsFeatureGate && artistId) {
              unsubscribeToArtist({
                input: {
                  artists: [artistId],
                },
              });
            }
          },
        },
      ).catch(error => {
        setFollowingStatus({
          userId,
          isFollowing: true,
        });

        logError({
          action: MISCELLANEOUS_ERROR_ACTIONS.FOLLOW_UNFOLLOW_ERROR,
          error,
          errorType: ERROR_TYPE.FETCH_GQL_ERROR,
          isArtistOnlyPage: false,
          level: 'error',
          message: 'Error calling UnfollowUserMutation',
          feature: FEATURES.FOLLOW_SUBSCRIBE,
          unindexedExtra: {
            userIdToFollowUnfollow: userId,
          },
          toast: 'Error unfollowing user',
        });
      });
    } else {
      trackClickEvent({
        event: 'Follow Clicked',
        properties: {
          associationId: userId,
          associationType: 'User',
          sourceComponent: sourceLocation,
        },
      });
      setFollowingStatus({
        userId,
        isFollowing: true,
      });

      followUserMutation(
        { input: { user: userId } },
        {
          onSuccess: async ({ data }) => {
            openToast({
              text: `You followed ${data.followUser.userB.username}.`,
              variant: 'success',
            });
          },
        },
      ).catch(error => {
        setFollowingStatus({
          userId,
          isFollowing: false,
        });

        logError({
          action: MISCELLANEOUS_ERROR_ACTIONS.FOLLOW_UNFOLLOW_ERROR,
          error,
          errorType: ERROR_TYPE.UNKNOWN,
          isArtistOnlyPage: false,
          level: 'error',
          message: 'Error calling FollowUserMutation',
          feature: FEATURES.FOLLOW_SUBSCRIBE,
          unindexedExtra: {
            userIdToFollowUnfollow: userId,
          },
          toast: 'Error following user',
        });
      });
    }
  };

  const onClick = async () => {
    // User is logged in
    if (authUser?.id) {
      await handleFollow();
      return;
    }
    // User is not logged in - make them Sign in
    trackClickEvent({
      event: 'Sign in Clicked',
      properties: {
        sourceComponent: 'Follow/Unfollow Button',
      },
    });
    login();
    return;
  };

  const buttonVariant = (() => {
    switch (variant) {
      case 'secondary':
        return followingStatus ? 'tertiary' : 'secondary';
      case 'tertiary':
      case 'notification':
        return followingStatus ? 'tertiary' : 'primary';
      default:
        return followingStatus ? 'tertiary' : 'primary';
    }
  })();

  return (
    <Button
      label={label}
      variant={dark ? 'tertiary' : buttonVariant}
      darkMode={
        !!followingStatus && followingStatus !== 'loading' ? dark && followingStatus : undefined
      }
      onClick={onClick}
      size={useIcon ? 'squared' : size}
      fullWidth={fullWidth}
      loading={loading}
      iconOnly={useIcon}
      leadingIcon={
        useIcon ? (
          <FontAwesomeIcon
            icon={!!followingStatus && followingStatus !== 'loading' ? faUserCheck : faUserPlus}
          />
        ) : undefined
      }
      className={
        !!className
          ? className
          : twMerge(
              variant === 'notification' &&
                !!followingStatus &&
                ' transition-all duration-500 ease-in-out hover:!bg-white hover:!shadow-tertiary',
              size === 'M' && 'h-9 w-[78px] min-w-[60px] font-medium',
              size === 'XS' && 'min-w-[80px]',
              useIcon && 'aspect-square h-9 w-9 min-w-[unset] p-0',
              useIcon && loading && '[&_.loading-circle]:h-4 [&_.loading-circle]:w-4',
            )
      }
    />
  );
};
